import React, { useState, useEffect } from 'react';
import { IoInformationCircleOutline, IoCheckmarkOutline, IoPlayCircleOutline, IoBookOutline, IoCopyOutline } from 'react-icons/io5';
import { HiChevronRight } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

export default function WithdrawalBank() {
    const { t } = useTranslation();
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [riskAccepted, setRiskAccepted] = useState(false);
    const [copiedState, setCopiedState] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        accountHolderName: '',
        tcNumber: '',
        amount: '',
        currency: 'USD',
        iban: '',
        bankName: ''
    });

    // Para birimi seçenekleri
    const currencies = [
        { symbol: 'USD', name: 'US Dollar', icon: '$' },
        { symbol: 'EUR', name: 'Euro', icon: '€' },
        { symbol: 'TRY', name: 'Turkish Lira', icon: '₺' }
    ];


    // Get user info on component mount
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                setLoading(true);
                const response = await api.getUserInfo();
                const userData = response.data?.data || response.data;
                // Map camelCase to PascalCase for consistency
                const normalizedData = {
                    ...userData,
                    Balance: userData.Balance ?? userData.balance,
                    Credit: userData.Credit ?? userData.credit,
                    FullName: userData.FullName ?? userData.fullName
                };
                setUserInfo(normalizedData);
            } catch (error) {
                console.error('Error fetching user info:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchUserInfo();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCurrencyChange = (currencySymbol) => {
        setSelectedCurrency(currencySymbol);
        setFormData(prev => ({
            ...prev,
            currency: currencySymbol
        }));
    };


    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(t('withdrawalBank.toast.infoCopied'));

            // Tik işareti göster
            setCopiedState(true);

            // 3 saniye sonra eski haline döndür
            setTimeout(() => {
                setCopiedState(false);
            }, 3000);
        } catch (err) {
            console.error('Kopyalama hatası:', err);
            toast.error(t('withdrawalBank.toast.copyFailed'));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.accountHolderName || !formData.tcNumber || !formData.amount || !formData.iban || !formData.bankName) {
            toast.error(t('withdrawalBank.toast.fillAllFields'));
            return;
        }

        if (parseFloat(formData.amount) <= 0) {
            toast.error(t('withdrawalBank.toast.amountGreaterThanZero'));
            return;
        }

        try {
            // CreateFiatWithdrawal API çağrısı
            const response = await api.createFiatWithdrawal({
                AccountOwner: formData.accountHolderName,
                IdentityNo: formData.tcNumber,
                Amount: formData.amount.toString(),
                Currency: formData.currency,
                BankName: formData.bankName,
                Iban: formData.iban
            });

            if (response.success) {
                toast.success(response.message || t('withdrawalBank.toast.bankWithdrawalRequestSuccess'));

                // Formu sıfırla
                setFormData({
                    accountHolderName: '',
                    tcNumber: '',
                    amount: '',
                    currency: 'USD',
                    iban: '',
                    bankName: ''
                });
            } else {
                toast.error(response.message || t('withdrawalBank.toast.withdrawalFailed'));
            }
        } catch (error) {
            console.error('Para çekme hatası:', error);
            
            // Özel hata mesajı kontrolü - "You can make your next withdrawal request starting from" içeriyorsa
            if (error.message && error.message.includes('You can make your next withdrawal request starting from')) {
                // Tarih kısmını çıkar (MM/DD/YYYY HH:MM:SS formatından DD/MM/YYYY HH:MM:SS formatına çevir)
                const dateMatch = error.message.match(/(\d{2})\/(\d{2})\/(\d{4}) (\d{2}:\d{2}:\d{2})/);
                if (dateMatch) {
                    const [, month, day, year, time] = dateMatch;
                    const formattedDate = `${day}/${month}/${year} ${time}`;
                    toast.error(t('withdrawal.toast.nextWithdrawalTime', { date: formattedDate }));
                } else {
                    toast.error(t('withdrawal.toast.nextWithdrawalTime', { date: error.message }));
                }
            } else {
                toast.error(t('withdrawalBank.toast.withdrawalFailed'));
            }
        }
    };

    return (
        <div className='deposit-layout'>
            <div className="container py-lg-5">
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="deposit-page">
                            {/* Header */}
                            <div className="deposit-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-3">
                                        <h1 className="deposit-title">{t('withdrawalBank.title')}</h1>
                                    </div>
                                    <Link to="/withdrawal" className="p2p-link d-flex align-items-center gap-2">
                                        <span>{t('withdrawalBank.cryptoWithdrawalLink')}</span>
                                        <HiChevronRight size={16} />
                                    </Link>
                                </div>
                            </div>

                            <div className="row g-4">
                                {/* Sol Kolon - Para Çekme İşlemleri */}
                                <div className="container">
                                    {/* Yatay Varlıklar Kartı */}
                                    <div className="assets-card-horizontal">
                                        <div className="assets-horizontal-grid">
                                            <div className="asset-balance-item left">
                                                <div className="asset-balance-label">
                                                    <span className="asset-dot available"></span>
                                                    {t('deposit.assets.title')}
                                                </div>
                                                <div className="asset-balance-value">
                                                    {loading ? '...' : `${((userInfo?.Balance || 0) + (userInfo?.Credit || 0)).toFixed(2)} USD`}
                                                </div>
                                            </div>

                                            <div className="asset-total-center">
                                                <div className="asset-total-label">{t('deposit.assets.availableBalance')}</div>
                                                <div className="asset-total-amount">
                                                    <span>{loading ? '...' : `${(userInfo?.Balance || 0).toFixed(2)} USD`}</span>
                                                </div>
                                            </div>

                                            <div className="asset-balance-item right">
                                                <div className="asset-balance-label">
                                                    <span className="asset-dot frozen"></span>
                                                    {t('deposit.assets.availableCredit')}
                                                </div>
                                                <div className="asset-balance-value">
                                                    <span>{loading ? '...' : `${(userInfo?.Credit || 0).toFixed(2)} USD`}</span>
                                                </div>  
                                            </div>
                                        </div>
                                    </div>

                                    <div className="deposit-actions">
                                        {/* 1. Para Birimi Seçimi */}
                                        <div className="deposit-step">
                                            <div className="step-number">1</div>
                                            <div className="step-content">
                                                <h3 className="step-title">{t('withdrawalBank.steps.selectCurrency')}</h3>
                                                <div className="coin-selector mt-lg-3">
                                                    <div className="coin-options">
                                                        {currencies.map((currency) => (
                                                            <button
                                                                key={currency.symbol}
                                                                className={`coin-option ${selectedCurrency === currency.symbol ? 'active' : ''}`}
                                                                onClick={() => handleCurrencyChange(currency.symbol)}
                                                            >
                                                                <span className="currency-icon">{currency.icon}</span>
                                                                <div className="coin-option-info">
                                                                    <span className="coin-option-symbol">{currency.symbol}</span>
                                                                    <span className="coin-option-name">{currency.name}</span>
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2. Form Alanları */}
                                        <div className="deposit-step">
                                            <div className="step-number">2</div>
                                            <div className="step-content">
                                                <h3 className="step-title">{t('withdrawalBank.steps.bankInfo')}</h3>

                                                {/* Hesap Sahibi Adı */}
                                                <div className="form-group mb-3 mt-lg-3">
                                                    <label className="form-label">{t('withdrawalBank.form.accountHolderNameLabel')}</label>
                                                    <input
                                                        type="text"
                                                        name="accountHolderName"
                                                        value={formData.accountHolderName}
                                                        onChange={handleInputChange}
                                                        placeholder={t('withdrawalBank.form.accountHolderNamePlaceholder')}
                                                        className="form-control"
                                                        required
                                                    />
                                                </div>

                                                {/* TC Kimlik No */}
                                                <div className="form-group mb-3">
                                                    <label className="form-label">{t('withdrawalBank.form.tcNumberLabel')}</label>
                                                    <input
                                                        type="text"
                                                        name="tcNumber"
                                                        value={formData.tcNumber}
                                                        onChange={handleInputChange}
                                                        placeholder={t('withdrawalBank.form.tcNumberPlaceholder')}
                                                        className="form-control"
                                                        maxLength="11"
                                                        required
                                                    />
                                                </div>

                                                {/* Miktar */}
                                                <div className="form-group mb-3">
                                                    <label className="form-label">{t('withdrawalBank.form.amountLabel')}</label>
                                                    <div className="currency-input-container">
                                                        <div className="currency-symbol-left">{currencies.find(c => c.symbol === selectedCurrency)?.icon || '$'}</div>
                                                        <input
                                                            type="number"
                                                            name="amount"
                                                            value={formData.amount}
                                                            onChange={handleInputChange}
                                                            placeholder={t('withdrawalBank.form.amountPlaceholder')}
                                                            step="0.01"
                                                            min="0"
                                                            className="currency-input"
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                {/* IBAN */}
                                                <div className="form-group mb-3">
                                                    <label className="form-label">{t('withdrawalBank.form.ibanLabel')}</label>
                                                    <input
                                                        type="text"
                                                        name="iban"
                                                        value={formData.iban}
                                                        onChange={handleInputChange}
                                                        placeholder={t('withdrawalBank.form.ibanPlaceholder')}
                                                        className="form-control"
                                                        required
                                                    />
                                                </div>

                                                {/* Banka Adı */}
                                                <div className="form-group mb-3">
                                                    <label className="form-label">{t('withdrawalBank.form.bankNameLabel')}</label>
                                                    <input
                                                        type="text"
                                                        name="bankName"
                                                        value={formData.bankName}
                                                        onChange={handleInputChange}
                                                        placeholder={t('withdrawalBank.form.bankNamePlaceholder')}
                                                        className="form-control"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* 3. Risk Uyarısı */}
                                        <div className="risk-warning">
                                            <h4 className="risk-title">{t('withdrawalBank.riskWarning.title')}</h4>
                                            <p className="risk-text">
                                                {t('withdrawalBank.riskWarning.description')}
                                            </p>
                                            <div className="risk-checkbox">
                                                <input
                                                    type="checkbox"
                                                    id="risk-accept"
                                                    checked={riskAccepted}
                                                    onChange={(e) => setRiskAccepted(e.target.checked)}
                                                />
                                                <label htmlFor="risk-accept">
                                                    {t('withdrawalBank.riskWarning.acceptWarning')}
                                                </label>
                                            </div>
                                        </div>

                                        {/* Onayla Butonu */}
                                        <div className="deposit-actions-footer">
                                            <button
                                                className={`confirm-btn ${riskAccepted ? 'active' : 'disabled'}`}
                                                disabled={!riskAccepted}
                                                onClick={handleSubmit}
                                            >
                                                {t('withdrawalBank.confirmButton')}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Sağ Kolon - Bilgiler */}
                                {/* <div className="col-lg-4">
                                    <div className="deposit-info"> */}
                                        
                                        {/* <div className="info-section">
                                            <h3 className="info-title">{t('withdrawalBank.assets.title')}</h3>
                                            <div className="assets-summary">
                                                <div className="total-assets">
                                                    {loading ? '...' : `${((userInfo?.Balance || 0) + (userInfo?.Credit || 0)).toFixed(2)} USD`}
                                                </div>
                                                <div className="assets-breakdown">
                                                    <div className="asset-item">
                                                        <div className="asset-dot available"></div>
                                                        <span>{t('withdrawalBank.assets.availableBalance')}:</span>
                                                        <span>{loading ? '...' : `${(userInfo?.Balance || 0).toFixed(2)} USD`}</span>
                                                    </div>
                                                    <div className="asset-item">
                                                        <div className="asset-dot frozen"></div>
                                                        <span>{t('withdrawalBank.assets.availableCredit')}:</span>
                                                        <span>{loading ? '...' : `${(userInfo?.Credit || 0).toFixed(2)} USD`}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* Dikkat Edilecek Noktalar */}
                                        {/* <div className="info-section">
                                            <h4 className="info-title">{t('withdrawalBank.attention.title')}</h4>
                                            <div className="attention-items">
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('withdrawalBank.attention.minWithdrawal')}:</span>
                                                    <span className="attention-value">100 USD</span>
                                                </div>
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('withdrawalBank.attention.transactionFee')}:</span>
                                                    <span className="attention-value">%0.5</span>
                                                </div>
                                                <div className="attention-item">
                                                    <span className="attention-label">{t('withdrawalBank.attention.processingTime')}:</span>
                                                    <span className="attention-value">1-3 iş günü</span>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* SSS */}
                                        {/* <div className="info-section">
                                            <div className="faq-header">
                                                <h4 className="info-title">{t('withdrawalBank.faq.title')}</h4>
                                            </div>
                                            <div className="faq-items">
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <span>{t('withdrawalBank.faq.processingTime')}</span>
                                                        <IoPlayCircleOutline size={16} />
                                                    </div>
                                                </div>
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <span>{t('withdrawalBank.faq.minAmount')}</span>
                                                        <IoPlayCircleOutline size={16} />
                                                    </div>
                                                </div>
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <span>{t('withdrawalBank.faq.supportedBanks')}</span>
                                                        <IoPlayCircleOutline size={16} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div> */}
                                    {/* </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: 'var(--card)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                    },
                    success: {
                        style: {
                            border: '1px solid #10b981',
                        },
                    },
                    error: {
                        style: {
                            border: '1px solid #ef4444',
                        },
                    },
                }}
            />
        </div>
    );
}
